import { useCallback, useMemo, useRef, useEffect } from 'react';

import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useVisit } from '@zenbusiness/application-commons-visit';

import AssistantWelcome from '#client/components/AssistantWelcome';
import ChatConnectionError from '#client/components/ChatConnectionError';
import ChatUI from '#client/components/ChatUI';
import Transition from '#client/components/Transition';
import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';
import * as ROUTES from '#client/constants/routes';
import { isValidWarmStartCode } from '#client/constants/warmStartMessages';
import { useDelayedLoading } from '#client/hooks/useDelayedLoading';
import { useInstructionRenderer } from '#client/hooks/useInstructionRenderer';

/**
 * ThreadViewer Component
 *
 * Handles the /guide/visitor/thread/:threadUuid route
 * Responsible for:
 * 1. Loading existing thread messages
 * 2. Continuing chat conversation
 * 3. Handling invalid/expired threadUuid (redirect to VisitorThreadCreator)
 * 4. Handling network/timeout errors with retry logic
 * 5. Auto-refreshing sidebar when arriving from thread creation
 */

export default function VisitorThreadViewer() {
  const { threadUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const enableVeloForVisitors = useExperimentFlag('enable-velo-threads-for-visitors', false);

  const { visitUuid } = useVisit();

  // Extract topic parameter from URL
  const topicCode = searchParams.get('topic');
  const isValidTopic = topicCode && isValidWarmStartCode(topicCode);

  // Redirect to dashboard if enableVeloForVisitors is false
  useEffect(() => {
    if (!enableVeloForVisitors) {
      window.location.replace('/');
    }
  }, [enableVeloForVisitors]);

  // Use the existing instruction renderer with the threadUuid from URL
  const {
    addUserResponse,
    currentMode,
    currentThreadUuid,
    isError,
    hasNextInstruction,
    initialized,
    isComplete,
    instructions,
    loading,
    loadingNext,
    loadExistingMessages,
    loadNextInstruction,
    resetStateForNewThread,
    retryConnection,
    sendUserFeedback,
    setInstructions
  } = useInstructionRenderer();

  // Debounced loading for thread switches - prevents flash on quick switches
  const showThreadLoading = useDelayedLoading(loading && !initialized, 250);

  // Ref to communicate with ActivePromptInput (for prompt button population)
  const activePromptInputRef = useRef(null);

  // Track whether we've already used the initial load flag to prevent reuse on refresh
  const hasUsedInitialLoadRef = useRef(false);

  const isInitialLoad = location.state?.isInitialLoad && !hasUsedInitialLoadRef.current;
  const effectiveMode = isInitialLoad ? 'initial' : currentMode;

  // Load existing messages on mount
  useEffect(() => {
    if (currentThreadUuid && currentThreadUuid !== threadUuid) {
      resetStateForNewThread(threadUuid);
    }

    if (visitUuid && threadUuid && !initialized) {
      loadExistingMessages(
        { visitUuid },
        threadUuid,
        'standalone' // Visitor thread, always standalone mode
      );
    }
  }, [
    currentThreadUuid,
    initialized,
    loadExistingMessages,
    resetStateForNewThread,
    threadUuid,
    visitUuid
  ]);

  // Only mark the initial load flag as used after we have instructions and have rendered
  // This prevents premature marking during the loading phase
  useEffect(() => {
    if (isInitialLoad && instructions.length > 0 && !loading) {
      hasUsedInitialLoadRef.current = true;
    }
  }, [isInitialLoad, instructions.length, loading]);

  // Determine if we should show welcome screen vs chat UI
  // Only show welcome when we're truly in an initial state (no thread loading, no instructions)
  const shouldShowWelcome = useMemo(() => {
    // Don't show welcome if we're loading a thread
    if (loading || showThreadLoading) {
      return false;
    }

    // Don't show welcome if we have instructions
    if (instructions.length > 0) {
      return false;
    }

    // Show welcome only when we're in a stable, empty state
    return initialized && !loading;
  }, [loading, showThreadLoading, instructions.length, initialized]);

  /**
   * Handler for when user sends a message (either typed or from prompt button)
   * @param message
   * @param messageType
   * @param agentName
   */
  const handleMessageSend = useCallback(
    async (message, messageType = 'text', agentName = null) => {
      await loadNextInstruction(message, messageType, 'standalone', { visitUuid, agentName });
    },
    [loadNextInstruction, visitUuid]
  );

  /**
   * Handler for user feedback
   * @param messageUuid
   * @param feedback
   */
  const handleUserFeedback = useCallback(
    async (messageUuid, feedback) => {
      // sendFeedback expects CustomerIdentity!, but visitor threads use VisitorIdentity
      // For now, this will fail for visitors - feedback may not be supported for anonymous users
      await sendUserFeedback({ visitUuid }, messageUuid, feedback, 'standalone');
    },
    [sendUserFeedback, visitUuid]
  );

  /**
   * Handler for when user clicks a prompt button
   * Instead of immediately sending, populate the input field
   * @param promptText
   */
  const handlePromptSelect = useCallback((promptText) => {
    // Use ref to directly set the input value without causing re-renders
    if (activePromptInputRef.current && activePromptInputRef.current.setValue) {
      activePromptInputRef.current.setValue(promptText);
    }
  }, []);

  // Handle start new
  const handleStartNew = useCallback(() => {
    const basePath = ROUTES.visitorThreadCreator;

    // Preserve topic parameter if present
    if (isValidTopic) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('topic', topicCode);
      navigate(`${basePath}?${newSearchParams.toString()}`, {
        replace: true
      });
    } else {
      navigate(basePath, {
        replace: true
      });
    }
  }, [navigate, isValidTopic, topicCode]);

  // Handle retry logic
  const handleRetry = useCallback(async () => {
    if (threadUuid) {
      // We have a threadUuid from URL, try to reload the existing thread
      if (retryConnection) {
        await retryConnection({ visitUuid }, 'standalone');
      }
    } else {
      // No threadUuid, start a new conversation instead
      handleStartNew();
    }
  }, [handleStartNew, retryConnection, threadUuid, visitUuid]);

  // Memoize the props to prevent unnecessary re-renders
  const chatUIProps = useMemo(
    () => ({
      activePromptInputRef,
      addUserResponse,
      currentMode: effectiveMode,
      isError,
      hasNextInstruction,
      instructions,
      isComplete,
      loading,
      loadingNext,
      onMessageSend: handleMessageSend,
      sendUserFeedback: handleUserFeedback,
      setInstructions
    }),
    [
      activePromptInputRef,
      addUserResponse,
      effectiveMode,
      isError,
      handleMessageSend,
      handleUserFeedback,
      hasNextInstruction,
      instructions,
      isComplete,
      loading,
      loadingNext,
      setInstructions
    ]
  );

  // Memoize the props to prevent unnecessary re-renders
  const welcomeProps = useMemo(
    () => ({
      onPromptSelect: handlePromptSelect,
      onMessageSend: handleMessageSend,
      activePromptInputRef
    }),
    [handlePromptSelect, handleMessageSend]
  );

  // Show loading immediately for entity loading (authentication/business setup)
  // Use debounced loading for thread loading to prevent flashes during switches
  if (showThreadLoading) {
    return <Transition type="THREAD_LOADING" />;
  }

  // Show error UI for initial load errors (no instructions yet)
  if (isError) {
    return (
      <ChatConnectionError
        instructions={instructions}
        onRetry={handleRetry}
        onStartNew={handleStartNew}
      />
    );
  }

  // Render appropriate content based on state
  return (
    <>
      <PageViewedTracker {...CLICKSTREAM_COMMON_PROPS} appPageName="thread_viewer" />
      {shouldShowWelcome ? <AssistantWelcome {...welcomeProps} /> : <ChatUI {...chatUIProps} />}
    </>
  );
}
